/**
 * Settings → Systems (docs/systems.md): the systems the data comes from and goes to, as the Cloud found them (a sender's
 * `system`, a [PREFIX] in a process or connection name, the start of a class name) and as admins described them (name,
 * kind, colour, logo or icon, aliases). Each card shows the data models in the system. The view (status=, q=, sort=) is in
 * the URL with replace; "Edit…" and "New system…" are windows that are history steps (`?system=<code>` / `?system=+`).
 * Each card shows the integrations in and out of its system; the system map (`view=map`, a history step) draws them as
 * arcs between the systems, the selected arc (`arc=`) replaced in the URL; a system on the map opens its card (`at=`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useSearchParams } from '../lib/liveparams.ts';
import { useAuth } from '../lib/auth.tsx';
import { Api, ApiError, type Integration } from '../lib/api.ts';
import { useAsync } from '../lib/hooks.ts';
import { useEnv } from '../lib/envs.tsx';
import { ago, count } from '../lib/format.ts';
import { useSyncedText } from '../lib/formstate.ts';
import { useUrlModal } from '../lib/urlmodal.ts';
import { useSystems } from '../lib/systemsdata.tsx';
import { systemColor, systemName, type SystemInfo, type SystemRoute, type SystemStatus } from '../lib/systems.ts';
import { KIND_LABEL, SORTS, STATUS_FILTERS, STATUS_LABEL, listSystems, readSystemsView, systemCounts, withSystemsView, type StatusFilter, type SystemSort, type SystemsView } from '../lib/systemedit.ts';
import { SystemEditor, SystemMark } from '../components/SystemEditor.tsx';
import { ProcessMenu, StatusDot, SystemMap } from '../components/SystemMap.tsx';
import { arcKey, integrationsOf, processQuery, readIntegrationsView, withIntegrationsView, type SystemsMode } from '../lib/integrations.ts';
import { Menu, Surface, cx, noun } from '../design/primitives.tsx';
import { CheckIcon, FilterIcon, PencilIcon, PlusIcon } from '../components/icons.tsx';
import { Button, Spinner, useToast } from '../components/ui.tsx';
import { Title } from './Settings.tsx';
import { Segmented } from '../components/FilterChips.tsx';

/** The sorts as the segmented control says them (systemedit.ts SORTS has the long names). */
const SORT_SHORT: Record<SystemSort, string> = { name: 'Name', code: 'Code', models: 'Data models', newest: 'Newest' };
const FILTER_LABEL: Record<StatusFilter, string> = { all: 'All', new: 'New', checked: 'Checked', hidden: 'Hidden' };
const readSystem = (p: URLSearchParams) => p.get('system') || undefined;
const withSystem = (p: URLSearchParams, v: string | undefined) => { const out = new URLSearchParams(p); if (v) out.set('system', v); else out.delete('system'); return out; };
const NEW = '+';
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const MODELS_SHOWN = 6;
const ROUTES_SHOWN = 4;
/** Integrations shown per direction on a card before "more" (failing ones always). */
const FLOWS_SHOWN = 3;
/** 120 000 → 120 k. */
const compact = (n: number) => (n >= 10_000 ? `${count(Math.round(n / 1000))} k` : count(n));

export function SystemsSettings() {
  const { tenant, canManage } = useAuth();
  const { current, link } = useEnv();
  const toast = useToast();
  const tid = tenant?.id ?? '';
  const registry = useSystems();
  // The data models of each system: the Cloud asks the Vaults (kept there for a while)
  const [loadKey, setLoadKey] = useState(0);
  const detail = useAsync(() => Api.systems(tid), [tid, current?.id, loadKey]);
  // The integrations between the systems over the last 7 days (the Cloud keeps them a few minutes)
  const flows = useAsync(() => Api.integrations(tid), [tid, current?.id]);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = readSystemsView(params);
  const iv = readIntegrationsView(params);
  // Cards or the map, the arc selected on the map: history steps
  const setMode = (mode: SystemsMode) => setParams((p) => withIntegrationsView(p, { mode, arc: null, at: null }));
  const selectArc = (arc: string | null) => setParams((p) => withIntegrationsView(p, { arc }));
  // A system on the map opens its card, with nothing filtering it out
  const openCard = (code: string) => setParams((p) => withIntegrationsView(withSystemsView(p, { status: 'all', q: '' }), { mode: 'cards', at: code }));
  const showArc = (from: string, to: string) => setParams((p) => withIntegrationsView(p, { mode: 'map', arc: arcKey(from, to) }));
  const searchProcess = (process: string) => void navigate(link(`/s?${new URLSearchParams({ q: processQuery(process), range: '7d' })}`));
  const allFlows = flows.data?.integrations;
  useEffect(() => {
    if (iv.mode !== 'cards' || !iv.at) return;
    const t = setTimeout(() => document.querySelector(`[data-system-card="${CSS.escape(iv.at!)}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
    return () => clearTimeout(t);
  }, [iv.mode, iv.at, registry.systems.size]);
  // A filter or a sort is a history step; typing the search replaces
  const change = useCallback((patch: Partial<SystemsView>, how: 'push' | 'replace' = 'push') => setParams((p) => withSystemsView(p, patch), how === 'replace' ? { replace: true } : undefined), [setParams]);
  const [text, setText] = useSyncedText(view.q, (q) => change({ q }, 'replace'));
  const editor = useUrlModal('system', readSystem, withSystem);
  const [confirmAll, setConfirmAll] = useState(false);

  // The registry (what the admins set, up to date after every edit) with the data models the detailed answer has
  const models = useMemo(() => new Map((detail.data?.systems ?? []).map((s) => [s.code, s.classes])), [detail.data]);
  const routes = useMemo(() => new Map((detail.data?.systems ?? []).map((s) => [s.code, s])), [detail.data]);
  const all = useMemo(() => [...registry.systems.values()], [registry.systems]);
  const modelsOf = useCallback((s: SystemInfo) => models.get(s.code) ?? s.classes, [models]);
  const counts = useMemo(() => systemCounts(all), [all]);
  const listed = useMemo(() => listSystems(all, view, modelsOf), [all, view, modelsOf]);
  const fresh = useMemo(() => all.filter((s) => s.status === 'new').map((s) => s.code), [all]);

  const markStatus = async (codes: string[], status: SystemStatus) => {
    try {
      const r = await Api.setSystemStatus(tid, codes, status);
      registry.applyStatus(codes, status);
      const n = r.changed.length;
      const word = status === 'new' ? 'as new again' : `as ${STATUS_LABEL[status].toLowerCase()}`;
      toast(n === 1 ? `${registry.systems.get(r.changed[0]!)?.name ?? r.changed[0]} marked ${word}` : `${count(n)} systems marked ${word}`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : String(e), 'fail');
    }
  };

  const editing = editor.value && editor.value !== NEW ? registry.systems.get(editor.value) : undefined;
  const taken = useMemo(() => {
    const out = new Set<string>();
    for (const s of all) if (s.code !== editing?.code) { out.add(s.code); s.aliases.forEach((a) => out.add(a)); }
    return out;
  }, [all, editing]);

  return (
    <>
      <Title action={canManage && <Button variant="primary" onClick={() => editor.open(NEW)}><PlusIcon size={14} />New system…</Button>}>Systems</Title>
      <div className="flex items-center gap-0.5 mb-4 p-0.5 rounded-full bg-n3 w-fit" role="group" aria-label="View">
        {(['cards', 'map'] as const).map((m) => (
          <button key={m} type="button" aria-pressed={iv.mode === m} onClick={() => { if (iv.mode !== m) setMode(m); }}
            className={cx('h-7 px-3 rounded-full border-0 cursor-pointer text-small whitespace-nowrap transition-colors duration-[var(--t-micro)]',
              iv.mode === m ? 'bg-n1 text-n10 font-medium shadow-[0_1px_2px_rgb(0_0_0/0.1)]' : 'bg-transparent text-n8 hover:text-n10')}>
            {m === 'cards' ? 'Systems' : 'System map'}
          </button>
        ))}
      </div>
      {iv.mode === 'map' && (
        <>
          <p className="m-0 mb-4 text-small text-n8 max-w-3xl">
            How data moves between the systems: each arrow is a process carrying data from the system it reads from to the systems it
            writes to, as its runs of the last 7 days show (the connection or the system of each step’s document).
          </p>
          {flows.error && <p role="alert" className="m-0 mb-3 text-small text-fail">{flows.error}</p>}
          {flows.data?.partial && <p className="m-0 mb-3 text-micro text-warn">Some Vaults did not answer: the map may be missing integrations.</p>}
          {!flows.data && flows.loading ? (
            <Surface className="p-10 grid place-items-center"><Spinner /></Surface>
          ) : flows.data && (
            <SystemMap integrations={flows.data.integrations} selected={iv.arc} onSelect={selectArc} onOpenSystem={openCard} onSearch={searchProcess} />
          )}
        </>
      )}
      {iv.mode === 'cards' && <>
      <p className="m-0 mb-5 text-small text-n8 max-w-3xl">
        Where the data lives. Systems are found in the data — the system a sender marks its data with, the [PREFIX] of process and
        connection names, or the start of a data model’s name — and you can name them, give them a colour, a logo or an icon, and
        tell which other codes mean the same system. The map, the badges and the connector’s list of systems use what you set here.
      </p>
      {fresh.length > 0 && (
        <div role="status" aria-label="New systems" className="mb-5 flex items-center gap-3 flex-wrap rounded-card bg-accent-soft px-4 py-3 text-small">
          <span className="size-2 rounded-full bg-accent shrink-0" aria-hidden="true" />
          {confirmAll ? (
            <>
              <span className="flex-1 min-w-[220px] text-n10">Mark {fresh.length === 1 ? 'the new system' : `all ${count(fresh.length)} new systems`} as checked? <span className="text-n8">A system that turns up later is new again.</span></span>
              <Button variant="ghost" onClick={() => setConfirmAll(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { setConfirmAll(false); void markStatus(fresh, 'checked'); }}>Mark as checked</Button>
            </>
          ) : (
            <>
              <span className="flex-1 min-w-[220px] text-n10"><span className="font-semibold">{count(fresh.length)} new system{fresh.length === 1 ? '' : 's'} to check</span>
                <span className="text-n8"> · found in the data{canManage ? ': name them, or mark them as checked' : ''}</span></span>
              {view.status !== 'new' && <Button onClick={() => change({ status: 'new' })}>Show them</Button>}
              {canManage && <Button variant="ghost" onClick={() => setConfirmAll(true)}>Mark all as checked…</Button>}
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-0.5 flex-wrap" role="group" aria-label="Show systems">
          {STATUS_FILTERS.map((k) => (
            <button key={k} type="button" aria-pressed={view.status === k} onClick={() => change({ status: k })}
              disabled={k !== 'all' && k !== view.status && counts[k] === 0}
              className={cx('h-7 px-2.5 rounded-full border-0 cursor-pointer text-small whitespace-nowrap transition-colors duration-[var(--t-micro)] disabled:opacity-40 disabled:cursor-default',
                view.status === k ? 'bg-accent-soft text-accent font-medium' : 'bg-transparent text-n8 hover:bg-n3 hover:text-n10')}>
              {FILTER_LABEL[k]} <span className="tabular-nums">{count(counts[k])}</span>
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 h-9 px-3 rounded-chip bg-n3 text-small text-n8 flex-1 min-w-48 max-w-xs">
          <FilterIcon size={14} /><span className="sr-only">Filter systems</span>
          <input className="flex-1 min-w-0 bg-transparent border-0 outline-none text-n10 placeholder:text-n7" placeholder="Filter by name, code, data model" value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        <Segmented className="ml-auto" label="Sort" ariaLabel="Sort systems" value={view.sort} onChange={(sort) => change({ sort })}
          options={(Object.keys(SORTS) as SystemSort[]).map((k) => [k, SORT_SHORT[k]] as const)} />
      </div>

      {all.length === 0 && (
        <Surface className="p-10 text-center text-small text-n8 flex flex-col items-center gap-2">
          {detail.loading ? <Spinner /> : <>No systems yet. They appear as data arrives{canManage ? ', or add one by hand' : ''}.</>}
        </Surface>
      )}
      {all.length > 0 && listed.length === 0 && (
        <Surface className="p-8 flex flex-col items-center gap-2 text-small text-n7 text-center">
          <span>No system matches.</span>
          <Button variant="ghost" onClick={() => { setText(''); change({ status: 'all', q: '' }); }}>Show all</Button>
        </Surface>
      )}
      {detail.data?.detecting && <p className="m-0 mb-3 text-micro text-n7">The Vaults are still being asked which data models are in which system…</p>}

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3" aria-label="Systems">
        {listed.map((s) => (
          <SystemCard key={s.code} s={s} models={modelsOf(s)} routes={routes.get(s.code)?.connections ?? []} warnings={routes.get(s.code)?.warnings ?? []} canManage={canManage} loading={detail.loading && !detail.data}
            modelLink={(cls) => link(`/settings/model?class=${encodeURIComponent(cls)}`)}
            flows={allFlows} marked={iv.at === s.code} onSearch={searchProcess} onShowArc={showArc}
            onEdit={() => editor.open(s.code)} onStatus={(st) => void markStatus([s.code], st)} />
        ))}
      </div>
      </>}

      {editor.value && canManage && (editor.value === NEW || editing) && (
        <SystemEditor key={editor.value} tid={tid} system={editing} taken={taken} onClose={editor.close}
          onSaved={(saved, was) => {
            registry.apply(saved, was);
            editor.close();
            setLoadKey((k) => k + 1);
            toast(was ? `${saved.name} saved` : `${saved.name} added`);
          }} />
      )}
    </>
  );
}

function StatusChip({ status }: { status: SystemStatus }) {
  return (
    <span className={cx('inline-flex items-center h-5 px-2 rounded-full text-micro font-medium whitespace-nowrap shrink-0',
      status === 'new' ? 'bg-accent-soft text-accent ring-1 ring-inset ring-accent/30' : status === 'hidden' ? 'bg-n3 text-n7' : 'bg-ok-soft text-ok')}>{STATUS_LABEL[status]}</span>
  );
}

function SystemCard({ s, models, routes, warnings, canManage, loading, modelLink, flows, marked, onSearch, onShowArc, onEdit, onStatus }: {
  s: SystemInfo; models: readonly string[]; routes: readonly SystemRoute[]; warnings: NonNullable<SystemInfo['warnings']>; canManage: boolean; loading: boolean; modelLink: (cls: string) => string;
  /** The integrations of the tenant (undefined while loading). */
  flows: readonly Integration[] | undefined;
  /** Opened from the map: marked. */
  marked: boolean;
  onSearch: (process: string) => void; onShowArc: (from: string, to: string) => void;
  onEdit: () => void; onStatus: (s: SystemStatus) => void;
}) {
  const kind = s.kind ? KIND_LABEL[s.kind] ?? s.kind : null;
  // The kind, unless it only repeats the code (ERP · ERP)
  const facts = [kind && kind.toUpperCase() !== s.code ? kind : null, s.vendor, s.owner ? `owner ${s.owner}` : null].filter(Boolean) as string[];
  return (
    <Surface className={cx('p-4 flex flex-col gap-3 min-w-0 scroll-mt-24', s.status === 'hidden' && 'opacity-60', marked && 'ring-2 ring-accent')} data-system-card={s.code}>
      <div className="flex items-start gap-3">
        <SystemMark code={s.code} color={systemColor(s.code)} icon={s.icon} logo={s.logo} size={44} />
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="m-0 text-body font-semibold truncate">{s.name}</h2>
            {s.status !== 'checked' && <StatusChip status={s.status} />}
          </div>
          <span className="text-micro text-n7 truncate">
            <span className="font-mono font-semibold text-n8">{s.code}</span>
            {s.aliases.length > 0 && <> · also <span className="font-mono">{s.aliases.join(', ')}</span></>}
            {facts.length > 0 && <> · {facts.join(' · ')}</>}
          </span>
        </div>
        {canManage && (
          <Menu.Root>
            <Menu.Trigger aria-label={`Status of ${s.name}`}
              className="size-8 rounded-chip grid place-items-center bg-transparent text-n8 border-0 cursor-pointer text-body leading-none hover:bg-n3 focus-visible:ring-2 focus-visible:ring-accent shrink-0">
              <span aria-hidden="true">⋯</span>
            </Menu.Trigger>
            <Menu.Content align="end" className="min-w-56">
              <Menu.Label>Status: {STATUS_LABEL[s.status]}</Menu.Label>
              {s.status !== 'checked' && <Menu.Item icon={<CheckIcon size={16} />} onSelect={() => onStatus('checked')}>Mark as checked</Menu.Item>}
              {s.status !== 'hidden' && <Menu.Item onSelect={() => onStatus('hidden')}>Hide (not offered to connectors)</Menu.Item>}
              {s.status !== 'new' && <Menu.Item onSelect={() => onStatus('new')}>Back to new</Menu.Item>}
            </Menu.Content>
          </Menu.Root>
        )}
      </div>
      {s.description && <p className="m-0 text-small text-n9 whitespace-pre-line line-clamp-3 [overflow-wrap:anywhere]">{s.description}</p>}
      <div className="flex flex-col gap-1.5">
        <span className="text-micro font-medium uppercase tracking-[.08em] text-n7">Data models</span>
        {models.length === 0 ? (
          <span className="text-small text-n7">{loading ? 'Looking…' : 'None seen yet'}</span>
        ) : (
          <div className="flex items-center gap-1.5 flex-wrap">
            {models.slice(0, MODELS_SHOWN).map((cls) => (
              <Link key={cls} to={modelLink(cls)} title={cls}
                className="inline-flex items-center h-6 px-2.5 rounded-full bg-n3 text-small text-n9 no-underline hover:bg-n4 hover:text-n10 max-w-full truncate">{cap(noun(cls, 2))}</Link>
            ))}
            {models.length > MODELS_SHOWN && <span className="text-small text-n7">+{count(models.length - MODELS_SHOWN)} more</span>}
          </div>
        )}
      </div>
      {routes.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-micro font-medium uppercase tracking-[.08em] text-n7" title="The technical routes into the system: one system can be reached over several connections">Connections</span>
          <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
            {routes.slice(0, ROUTES_SHOWN).map((r) => (
              <li key={r.name} className="flex items-baseline gap-2 text-small min-w-0">
                <span className="truncate text-n9" title={r.name}>{r.name}</span>
                <span className="ml-auto shrink-0 text-micro text-n7 tabular-nums whitespace-nowrap">{compact(r.count)}{r.last ? ` · ${ago(r.last)}` : ''}</span>
              </li>
            ))}
          </ul>
          {routes.length > ROUTES_SHOWN && <span className="text-micro text-n7">+{count(routes.length - ROUTES_SHOWN)} more</span>}
        </div>
      )}
      <SystemFlows code={s.code} flows={flows} onSearch={onSearch} onShowArc={onShowArc} />
      {warnings.map((w) => (
        <p key={w.connection} role="note" className="m-0 flex items-start gap-1.5 text-micro text-n8">
          <span className="mt-[5px] size-1.5 rounded-full bg-warn shrink-0" aria-hidden="true" />
          <span>Connection <span className="font-medium text-n9">{w.connection}</span> is named for {systemName(w.namedFor)}, but the data says {systemName(w.dataSays)}</span>
        </p>
      ))}
      <div className="flex items-center gap-2 flex-wrap mt-auto pt-1">
        <span className="text-micro text-n7 mr-auto">
          {s.source === 'manual' ? 'Added by hand' : `Found ${ago(s.firstSeenAt)}`}
          {s.reviewedAt && s.status !== 'new' ? ` · ${s.status === 'hidden' ? 'hidden' : 'checked'} ${ago(s.reviewedAt)}${s.reviewedBy ? ` by ${s.reviewedBy}` : ''}` : ''}
        </span>
        {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-small text-accent no-underline hover:underline">Open</a>}
        {canManage && s.status === 'new' && <Button onClick={() => onStatus('checked')}><CheckIcon size={14} />Mark as checked</Button>}
        {canManage && <Button onClick={onEdit}><PencilIcon size={14} />Edit…</Button>}
      </div>
    </Surface>
  );
}

/** The integrations of a system on its card: what comes in (from which system, by which process) and what goes out. */
function SystemFlows({ code, flows, onSearch, onShowArc }: {
  code: string; flows: readonly Integration[] | undefined; onSearch: (process: string) => void; onShowArc: (from: string, to: string) => void;
}) {
  const mine = useMemo(() => integrationsOf(flows ?? [], code), [flows, code]);
  const [more, setMore] = useState(false);
  if (!flows) return null;
  const total = mine.in.length + mine.out.length + mine.within.length;
  if (total === 0) return null;
  const row = (i: Integration, dir: 'in' | 'out' | 'within') => {
    const other = dir === 'in' ? i.from : dir === 'out' ? i.to : null;
    const where = other ? <><span className="text-n7">{dir === 'in' ? 'from' : 'to'}</span> <span className="font-medium text-n10">{systemName(other)}</span></>
      : <span className="text-n7">{dir === 'in' ? 'only written to' : dir === 'out' ? 'only read from' : 'within'} {systemName(code)}</span>;
    return (
      <li key={`${dir}|${i.process}|${other ?? ''}`} className={cx('grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-2 text-small rounded-chip -mx-1.5 px-1.5 py-0.5',
        i.status === 'failing' && 'bg-fail-soft')}>
        <span className="self-center"><StatusDot status={i.status} /></span>
        <span className="min-w-0 flex items-baseline gap-1 overflow-hidden">
          <span className="whitespace-nowrap shrink-0">{where}</span>
          <span className="text-n7 shrink-0">via</span>
          <ProcessMenu process={i.process} onSearch={onSearch} className={i.status === 'failing' ? 'text-fail font-medium' : 'text-n9'}
            extra={i.from && i.to && i.from !== i.to ? <Menu.Item onSelect={() => onShowArc(i.from!, i.to!)}>Show on the system map</Menu.Item> : undefined}>
            {stripPrefix(i.process)}
          </ProcessMenu>
        </span>
        <span className="text-micro text-n7 tabular-nums whitespace-nowrap" title={`${count(i.runs)} runs in the last 7 days${i.failed ? `, ${count(i.failed)} with failures` : ''}`}>
          {i.status === 'failing' ? <span className="text-fail">failing</span> : i.status === 'quiet' ? <span className="text-warn">quiet</span> : `${compact(i.runs)} runs`}{i.lastRun ? ` · ${ago(i.lastRun)}` : ''}
        </span>
      </li>
    );
  };
  const groups: [string, 'in' | 'out' | 'within', Integration[]][] = [['In', 'in', mine.in], ['Out', 'out', mine.out], ['Within', 'within', mine.within]];
  const shownOf = (list: Integration[]) => (more ? list : list.slice(0, Math.max(FLOWS_SHOWN, list.filter((i) => i.status === 'failing').length)));
  const hidden = groups.reduce((n, [, , list]) => n + list.length - shownOf(list).length, 0);
  return (
    <div className="flex flex-col gap-1" aria-label={`Integrations of ${systemName(code)}`}>
      <span className="text-micro font-medium uppercase tracking-[.08em] text-n7" title="Processes moving data in and out of the system, from their runs of the last 7 days">Integrations</span>
      {groups.map(([label, dir, list]) => {
        if (list.length === 0) return null;
        const shown = shownOf(list);
        return (
          <div key={dir} className="flex flex-col gap-0.5">
            <span className="text-micro text-n7">{label}</span>
            <ul className="m-0 p-0 list-none flex flex-col gap-0.5">{shown.map((i) => row(i, dir))}</ul>
          </div>
        );
      })}
      {(more || hidden > 0) && (
        <button type="button" className="self-start bg-transparent border-0 p-0 cursor-pointer text-micro text-accent hover:underline" onClick={() => setMore((m) => !m)}>
          {more ? 'Show fewer' : `+${count(hidden)} more`}
        </button>
      )}
    </div>
  );
}

const stripPrefix = (name: string) => name.replace(/^\s*\[[^\]]{1,24}\]\s*/, '');
